import * as signalR from '@microsoft/signalr'
import useDefaultDevice from 'hooks/useDefaultDevice/useDefaultDevice'
import { useEffect, useCallback, useRef } from 'react'
import toast from 'utilities/toast/toast'
import { deviceStatusSubjects } from '../hwAgentSubjects'
import {
    CallerIdMessage,
    DeviceStatusEventType,
    DeviceStatusResult,
    DeviceType,
    PrinterSettings,
} from './types'
import printerService from 'Services/api/printer.service'
import posEvent, { POSEventType } from 'websockets/events/posHubEvent'
import getEnv from 'utilities/getEnv'
import usePrinterStatusHubReducer from './usePrinterStatusHubReducer'
import { convertPrinterContractToPrinterSetting } from './utils/convertPrinterContractToPrinterSetting'
import useDeviceHardwareAgentCaller from 'hooks/useHardwareAgentCaller/useDeviceHardwareAgentCaller'
import Version from '../version'
import { isNil } from 'lodash-es'
import errorMessages from 'utilities/errorMessages'
import { defaultSignalRReconnectionDelay } from 'utilities/timeoutHelper'

const LOOP_TIME_SECOND = 10000

const hwAgentLastCompatibilityVersion = getEnv(
    'REACT_APP_HARDWARE_AGENT_LAST_COMPATIBLE_VERSION',
)
const hwAgentLatestVersion = getEnv('REACT_APP_HARDWARE_AGENT_LATEST_VERSION')

function UpdateSubscribedPrinter(
    hubClientRef: signalR.HubConnection | null,
    newPrinterSettings: PrinterSettings[],
) {
    hubClientRef?.invoke('UpdateSubscribedPrinterStatus', newPrinterSettings)
}
function DeleteSubscribedPrinter(
    hubClientRef: signalR.HubConnection | null,
    removedPrinterSettingIds: string[],
) {
    hubClientRef?.invoke(
        'DeleteSubscribedPrinterStatus',
        removedPrinterSettingIds,
    )
}
function usePrinterDeviceStatusHub() {
    const manualRetryConnection = useRef<NodeJS.Timeout | undefined>()

    const { dispatch, state } = usePrinterStatusHubReducer()

    const { Disconnected, Reconnecting } = signalR.HubConnectionState
    const {
        printingHardwareAgentUri,
        enableHardwareAgentPrinting,
    } = useDefaultDevice()
    const hubClientRef = useRef<signalR.HubConnection | null>()
    const printingCurrentHardwareAgentUriConnected = useRef<string | null>()

    const validateHWagentConnected = useCallback(() => {
        return (
            hubClientRef.current &&
            hubClientRef.current.state === signalR.HubConnectionState.Connected
        )
    }, [])

    const subscribePrinterStatus = useCallback(async () => {
        if (!validateHWagentConnected()) return

        try {
            const printers = (await printerService.getAllPrinter()).data
            const checkablePrinters: PrinterSettings[] = printers
                .filter((p) => !p.onScreenPrinter)
                .map((printer) =>
                    convertPrinterContractToPrinterSetting(printer),
                )

            if (checkablePrinters.length === 0) {
                deviceStatusSubjects.next({
                    eventType: DeviceStatusEventType.NoPrinterSetting,
                    data: null,
                })
                return
            }
            UpdateSubscribedPrinter(hubClientRef.current!, checkablePrinters)
        } catch (err: any) {
            console.log(
                '[HW Agent PrinterHub] subscribePrinterStatus err:',
                err,
            )
        }
    }, [validateHWagentConnected])

    const onHWAgentConnected = useCallback(() => {
        dispatch({ type: 'HW_AGENT_ONLINE' })
        subscribePrinterStatus()
        printingCurrentHardwareAgentUriConnected.current = printingHardwareAgentUri
    }, [dispatch, printingHardwareAgentUri, subscribePrinterStatus])

    const onHWAgentDisconnected = useCallback(
        (showToast: boolean = true) => {
            dispatch({ type: 'HW_AGENT_OFFLINE' })
            showToast && toast.error('PrinterHub is offline.')
            deviceStatusSubjects.next({
                eventType: DeviceStatusEventType.Disconnected,
                data: null,
            })
        },
        [dispatch],
    )

    // Subscribe POSHub event subject
    useEffect(() => {
        const subscription = posEvent.subscribe((message) => {
            if (!validateHWagentConnected()) return

            if (message.type === POSEventType.OnPrinterSettingChanged) {
                const newPrinterSetting = convertPrinterContractToPrinterSetting(
                    message.params,
                )
                UpdateSubscribedPrinter(hubClientRef.current!, [
                    newPrinterSetting,
                ])
            } else if (message.type === POSEventType.OnPrinterSettingRemoved) {
                const removedPrinterSettingIds = message.params
                DeleteSubscribedPrinter(
                    hubClientRef.current!,
                    removedPrinterSettingIds,
                )
            } else if (message.type === POSEventType.OnPOSHubReconnected) {
                // because the connection ID of POS Hub change, we have to update the connectivity table again on CloudAPI
                subscribePrinterStatus()
            }
        })

        return () => {
            subscription.unsubscribe()
        }
    }, [subscribePrinterStatus, validateHWagentConnected])
    const handleConnectToHardwareAgent = useCallback(() => {
        hubClientRef.current = new signalR.HubConnectionBuilder()
            .withUrl(`${printingHardwareAgentUri}/hubs/pos-client`)
            .configureLogging(signalR.LogLevel.None)
            .withAutomaticReconnect(defaultSignalRReconnectionDelay)
            .build()

        hubClientRef.current.onreconnected((connectionId?: string) => {
            console.log(
                '[HW Agent PrinterHub] reconnected - connection Id = ' +
                    connectionId,
            )
            onHWAgentConnected()
            toast.success('PrinterHub is back to online.')
        })
        hubClientRef.current.onreconnecting((err?: Error) => {
            onHWAgentDisconnected(true)
        })

        // Starts the SignalR connection
        hubClientRef.current
            .start()
            .then(() => {
                if (!hubClientRef.current) return

                console.log(
                    '[HW Agent PrinterHub] connected - connection Id = ' +
                        hubClientRef.current.connectionId,
                )
                onHWAgentConnected()
                toast.success('PrinterHub is connected')

                hubClientRef.current.on(
                    'OnUpdateStatus',
                    (data: DeviceStatusResult) => {
                        if (data.type === DeviceType.Printer)
                            // push message to Shared ReactiveX Subject
                            deviceStatusSubjects.next({
                                eventType:
                                    DeviceStatusEventType.SingleStatusCheck,
                                data,
                            })
                    },
                )

                hubClientRef.current.on(
                    'OnPulseBatchStatus',
                    (data: DeviceStatusResult[]) => {
                        deviceStatusSubjects.next({
                            eventType: DeviceStatusEventType.OnPulseBatchStatus,
                            data,
                        })
                    },
                )

                hubClientRef.current.on(
                    'OnSelectiveUpdateStatus',
                    (data: DeviceStatusResult[]) => {
                        deviceStatusSubjects.next({
                            eventType:
                                DeviceStatusEventType.OnSelectiveUpdateStatus,
                            data,
                        })
                    },
                )

                hubClientRef.current.on(
                    'OnReceiveCallerId',
                    (data: CallerIdMessage) => {
                        console.log('OnReceiveCallerId', data)
                        deviceStatusSubjects.next({
                            eventType: DeviceStatusEventType.OnReceiveCallerId,
                            data,
                        })
                    },
                )
            })
            .catch(() => {
                // this will prevent sending an error log to Sentry, when HWAgent is offline
                // log here if needed...

                // fire disconnected in-case cannot connect to HW Agent at all
                // this will help syncing the HWAgent status to Cloud API
                onHWAgentDisconnected(false)
            })
    }, [onHWAgentConnected, onHWAgentDisconnected, printingHardwareAgentUri])
    useEffect(() => {
        if (
            enableHardwareAgentPrinting &&
            (printingHardwareAgentUri !==
                printingCurrentHardwareAgentUriConnected.current ||
                !hubClientRef.current)
        ) {
            handleConnectToHardwareAgent()
        }

        return () => {
            console.log('[HW Agent PrinterHub] clean up useDeviceStatusHub')
            onHWAgentDisconnected(false)
            hubClientRef.current?.stop()
            hubClientRef.current = null
        }
    }, [
        enableHardwareAgentPrinting,
        handleConnectToHardwareAgent,
        onHWAgentDisconnected,
        printingHardwareAgentUri,
    ])
    const cleanLoopRetry = useCallback(() => {
        if (manualRetryConnection.current)
            clearInterval(manualRetryConnection.current)
    }, [])
    const loopRetryProcess = useCallback(
        (func: () => void) => {
            cleanLoopRetry()
            const attemptToStartConnection = () => {
                func()
            }
            manualRetryConnection.current = setInterval(
                () => attemptToStartConnection(),
                LOOP_TIME_SECOND,
            )
        },
        [cleanLoopRetry],
    )
    useEffect(() => {
        if (!enableHardwareAgentPrinting) return
        // Manually attempt to reconnect the signalR in case of losing connection whether due to the absence of
        // the HWAgent or the terminal itself is disconnected from the network
        if (!hubClientRef) {
            loopRetryProcess(handleConnectToHardwareAgent)
            return
        } else if (
            hubClientRef.current &&
            hubClientRef.current.state === Disconnected
        ) {
            loopRetryProcess(() => {
                hubClientRef
                    .current!.start()
                    .then(() => onHWAgentConnected())
                    .catch(() => {
                        // this will prevent sending this error log to Sentry, when HWAgent is offline
                        // log here if needed
                    })
            })
            return
        } else {
            cleanLoopRetry()
        }
        return () => {
            // clean up the setTimeout when this is unmounted
            cleanLoopRetry()
        }
    }, [
        Disconnected,
        Reconnecting,
        enableHardwareAgentPrinting,
        handleConnectToHardwareAgent,
        onHWAgentConnected,
        loopRetryProcess,
        cleanLoopRetry,
    ])

    const { getVersionRequest } = useDeviceHardwareAgentCaller(
        printingHardwareAgentUri,
    )
    const checkHWAgentVersion = useCallback(async () => {
        try {
            const hwAgentVersion = await getVersionRequest()
            dispatch({
                type: 'SET_HW_AGENT_VERSION',
                payload: {
                    version: hwAgentVersion?.data.version,
                    compatibilityVersion: hwAgentLastCompatibilityVersion,
                    latestVersion: hwAgentLatestVersion,
                },
            })
            // Check if the hardware agent is compatible with the current version
            if (
                isNil(hwAgentVersion?.data?.version) ||
                isNil(hwAgentLastCompatibilityVersion) ||
                isNil(hwAgentLatestVersion)
            )
                return

            const version = new Version(hwAgentVersion?.data?.version ?? '')
            dispatch({
                type: 'SET_HW_AGENT_VERSION_STATUS',
                payload: {
                    isUncompatibleVersion: version.isOlder(
                        hwAgentLastCompatibilityVersion,
                    ),
                    isUnderLatestVersion: version.isOlder(hwAgentLatestVersion),
                },
            })
        } catch (err) {
            let { message, arg } = errorMessages(
                err,
                'Unable connect to check Printer HwAgent version',
            )
            toast.error(message, arg)
        }
    }, [dispatch, getVersionRequest])

    useEffect(() => {
        if (state.isOnline) {
            checkHWAgentVersion()
        }
    }, [checkHWAgentVersion, state.isOnline])

    return { ...state }
}

export default usePrinterDeviceStatusHub
